import { useState, useRef } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Alert,
  Box,
  Button,
  Snackbar,
  Tab,
  Tabs,
} from '@mui/material';
import {
  Download as DownloadIcon,
  Upload as UploadIcon,
} from '@mui/icons-material';
import { teamApi } from '@/services/api';
import { getErrorMessage } from '@/utils/errors';
import { useAuth } from '@/context/AuthContext';
import type { TeamImportData } from '@/types';
import MembersTable from '@/components/team/MembersTable';
import DocumentationTable from '@/components/team/DocumentationTable';
import RepositoriesTable from '@/components/team/RepositoriesTable';
import ExpertsTable from '@/components/team/ExpertsTable';
import ResponsibilitiesTable from '@/components/team/ResponsibilitiesTable';
import TeamLeadEscalationPanel from '@/components/team/TeamLeadEscalationPanel';

function TabPanel({ children, value, index }: { children: React.ReactNode; value: number; index: number }) {
  return (
    <div role="tabpanel" hidden={value !== index}>
      {value === index && <Box sx={{ pt: 2 }}>{children}</Box>}
    </div>
  );
}

export default function TeamKnowledgeTab() {
  const { teamId } = useParams<{ teamId: string }>();
  const [searchParams] = useSearchParams();
  const initialTab = parseInt(searchParams.get('tab') || '0', 10);
  const [tabValue, setTabValue] = useState(initialTab);
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' }>({
    open: false,
    message: '',
    severity: 'success',
  });
  const [isExporting, setIsExporting] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const queryClient = useQueryClient();
  const { isTeamLead } = useAuth();
  const canEdit = isTeamLead(teamId!);

  const importMutation = useMutation({
    mutationFn: (data: TeamImportData) =>
      teamApi.importTeam(teamId!, data).then((res) => res.data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['team-members', teamId] });
      queryClient.invalidateQueries({ queryKey: ['team-documentation', teamId] });
      queryClient.invalidateQueries({ queryKey: ['team-repositories', teamId] });
      queryClient.invalidateQueries({ queryKey: ['team-experts', teamId] });
      queryClient.invalidateQueries({ queryKey: ['team-responsibilities', teamId] });
      setSnackbar({ open: true, message: 'Import complete!', severity: 'success' });
    },
    onError: (error: Error) => {
      setSnackbar({ open: true, message: `Import failed: ${getErrorMessage(error)}`, severity: 'error' });
    },
  });

  const handleExport = async () => {
    setIsExporting(true);
    try {
      const response = await teamApi.exportTeam(teamId!);
      const data = response.data;
      const exportData = {
        members: data.members.map((m: Record<string, unknown>) => ({
          email: m.email, display_name: m.display_name, role: m.role,
        })),
        documentation: data.documentation.map((d: Record<string, unknown>) => ({
          url: d.url, title: d.title, doc_type: d.doc_type, description: d.description,
        })),
        repositories: data.repositories.map((r: Record<string, unknown>) => ({
          url: r.url, name: r.name, platform: r.platform, description: r.description,
          default_branch: r.default_branch, language: r.language, is_primary: r.is_primary,
          auto_create_jira: r.auto_create_jira,
        })),
        experts: data.experts.map((e: Record<string, unknown>) => ({
          expert_name: e.expert_name, expert_email: e.expert_email, expert_tag: e.expert_tag,
          expertise: e.expertise, keywords: e.keywords, knowledge_level: e.knowledge_level,
          availability: e.availability,
        })),
        responsibilities: data.responsibilities.map((r: Record<string, unknown>) => ({
          component: r.component, responsible_team: r.responsible_team,
          contact_space_url: r.contact_space_url, escalation_email: r.escalation_email,
          keywords: r.keywords,
        })),
        exported_at: new Date().toISOString(),
        version: '1.0',
      };
      const blob = new Blob([JSON.stringify(exportData, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `team-backup.json`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      setSnackbar({ open: true, message: 'Team data exported successfully!', severity: 'success' });
    } catch (error) {
      setSnackbar({ open: true, message: `Export failed: ${getErrorMessage(error)}`, severity: 'error' });
    } finally {
      setIsExporting(false);
    }
  };

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const content = e.target?.result as string;
        const importData = JSON.parse(content);
        const teamImportData: TeamImportData = {
          members: importData.members?.map((m: Record<string, unknown>) => ({
            email: m.email, display_name: m.display_name, role: m.role,
          })),
          documentation: importData.documentation?.map((d: Record<string, unknown>) => ({
            url: d.url, title: d.title, doc_type: d.doc_type, description: d.description,
          })),
          repositories: importData.repositories?.map((r: Record<string, unknown>) => ({
            url: r.url, name: r.name, platform: r.platform, description: r.description,
            default_branch: r.default_branch, language: r.language, is_primary: r.is_primary,
          })),
          experts: importData.experts?.map((exp: Record<string, unknown>) => ({
            expert_name: exp.expert_name, expert_email: exp.expert_email, expert_tag: exp.expert_tag,
            expertise: exp.expertise, keywords: exp.keywords, knowledge_level: exp.knowledge_level,
            availability: exp.availability,
          })),
          responsibilities: importData.responsibilities?.map((r: Record<string, unknown>) => ({
            component: r.component, responsible_team: r.responsible_team,
            contact_space_url: r.contact_space_url, escalation_email: r.escalation_email,
            keywords: r.keywords,
          })),
        };
        importMutation.mutate(teamImportData);
      } catch {
        setSnackbar({ open: true, message: 'Failed to parse file. Please use a valid JSON export.', severity: 'error' });
      }
    };
    reader.readAsText(file);
    if (event.target) event.target.value = '';
  };

  const { data: members } = useQuery({
    queryKey: ['team-members', teamId],
    queryFn: () => teamApi.getMembers(teamId!).then((res) => res.data),
    enabled: !!teamId,
  });

  const { data: documentation } = useQuery({
    queryKey: ['team-documentation', teamId],
    queryFn: () => teamApi.getDocumentation(teamId!).then((res) => res.data),
    enabled: !!teamId,
  });

  const { data: repositories } = useQuery({
    queryKey: ['team-repositories', teamId],
    queryFn: () => teamApi.getRepositories(teamId!).then((res) => res.data),
    enabled: !!teamId,
  });

  const { data: experts } = useQuery({
    queryKey: ['team-experts', teamId],
    queryFn: () => teamApi.getExperts(teamId!).then((res) => res.data),
    enabled: !!teamId,
  });

  const { data: responsibilities } = useQuery({
    queryKey: ['team-responsibilities', teamId],
    queryFn: () => teamApi.getResponsibilities(teamId!).then((res) => res.data),
    enabled: !!teamId,
  });

  const handleNotify = (message: string, severity: 'success' | 'error') => {
    setSnackbar({ open: true, message, severity });
  };

  if (!teamId) return null;

  return (
    <Box>
      {canEdit && (
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, mb: 2 }}>
          <Button
            variant="outlined"
            size="small"
            startIcon={<DownloadIcon />}
            onClick={handleExport}
            disabled={isExporting}
          >
            {isExporting ? 'Exporting...' : 'Export'}
          </Button>
          <Button
            variant="outlined"
            size="small"
            startIcon={<UploadIcon />}
            onClick={() => fileInputRef.current?.click()}
            disabled={importMutation.isPending}
          >
            {importMutation.isPending ? 'Importing...' : 'Import'}
          </Button>
          <input
            type="file"
            accept=".json"
            ref={fileInputRef}
            onChange={handleFileSelect}
            style={{ display: 'none' }}
          />
        </Box>
      )}
      <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
        <Tabs value={tabValue} onChange={(_, v) => setTabValue(v)} variant="scrollable" scrollButtons="auto">
          <Tab label={`Members (${members?.length || 0})`} />
          <Tab label={`Documentation (${documentation?.length || 0})`} />
          <Tab label={`Repositories (${repositories?.length || 0})`} />
          <Tab label={`Experts (${experts?.length || 0})`} />
          <Tab label={`Teams Matrix (${responsibilities?.length || 0})`} />
          <Tab label="Team Lead & Escalation" />
        </Tabs>
      </Box>

      <TabPanel value={tabValue} index={0}>
        <MembersTable teamId={teamId} onNotify={handleNotify} />
      </TabPanel>

      <TabPanel value={tabValue} index={1}>
        <DocumentationTable teamId={teamId} onNotify={handleNotify} />
      </TabPanel>

      <TabPanel value={tabValue} index={2}>
        <RepositoriesTable teamId={teamId} onNotify={handleNotify} />
      </TabPanel>

      <TabPanel value={tabValue} index={3}>
        <ExpertsTable teamId={teamId} onNotify={handleNotify} />
      </TabPanel>

      <TabPanel value={tabValue} index={4}>
        <ResponsibilitiesTable teamId={teamId} onNotify={handleNotify} />
      </TabPanel>

      <TabPanel value={tabValue} index={5}>
        <TeamLeadEscalationPanel teamId={teamId} onNotify={handleNotify} />
      </TabPanel>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={5000}
        onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
